import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { ShortLink } from '@shared/types';
import { EmptyState } from '../components/EmptyState';
import { Spinner } from '../components/Spinner';
import { IconCopy, IconExternal, IconLink, IconPlus, IconSearch } from '../components/Icons';
import { formatNumber, formatRelative, shortUrlFor } from '../lib/format';
import { useToast } from '../lib/toast';

interface ListResult {
  items: ShortLink[];
  total: number;
  page: number;
  pageSize: number;
}

export function LinksPage() {
  const [data, setData] = useState<ListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'active' | 'disabled'>('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', '20');
    if (search.trim()) params.set('search', search.trim());
    if (status) params.set('status', status);
    return params.toString();
  }, [page, search, status]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get<ListResult>(`/api/links?${queryString}`)
        .then(setData)
        .catch((err) => setError(err instanceof Error ? err.message : String(err)))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [queryString]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Links</h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Manage and monitor every short link in your workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/api/links/export.csv" className="btn-secondary">
            Export CSV
          </a>
          <Link to="/app/links/new" className="btn-primary">
            <IconPlus />
            New link
          </Link>
        </div>
      </header>

      <div className="card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by code, title, destination..."
            className="input pl-9"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | 'active' | 'disabled');
            setPage(1);
          }}
          className="input max-w-[160px]"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}

      {data && data.items.length === 0 && !loading && (
        <EmptyState
          icon={<IconLink />}
          title="No links match your filters"
          description="Try removing filters or create a new link to get started."
          action={
            <Link to="/app/links/new" className="btn-primary">
              <IconPlus />
              New link
            </Link>
          }
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Title / Destination</th>
                  <th>Status</th>
                  <th>Clicks</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((l) => (
                  <tr key={l.id}>
                    <td className="font-mono text-xs">
                      <Link
                        to={`/app/links/${l.id}`}
                        className="text-brand-600 dark:text-brand-300 hover:underline"
                      >
                        {l.code}
                      </Link>
                    </td>
                    <td className="max-w-md">
                      {l.title && <div className="font-medium truncate">{l.title}</div>}
                      <div
                        className="text-xs text-ink-500 dark:text-ink-400 truncate"
                        title={l.destinationUrl}
                      >
                        {l.destinationUrl}
                      </div>
                    </td>
                    <td>
                      <span
                        className={l.status === 'active' ? 'badge-success' : 'badge-muted'}
                      >
                        {l.status}
                      </span>
                    </td>
                    <td>{formatNumber(l.clickCount)}</td>
                    <td className="text-ink-500 dark:text-ink-400 text-xs">
                      {formatRelative(l.createdAt)}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(shortUrlFor(l.code));
                            toast.success('Short URL copied');
                          } catch {
                            toast.error('Could not copy to clipboard');
                          }
                        }}
                        title="Copy short URL"
                      >
                        <IconCopy />
                      </button>
                      <a
                        className="btn-ghost"
                        href={shortUrlFor(l.code)}
                        target="_blank"
                        rel="noreferrer"
                        title="Open short URL"
                      >
                        <IconExternal />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-ink-500 dark:text-ink-400">
              {formatNumber(data.total)} total
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <div className="text-sm">
                {data.page} / {totalPages}
              </div>
              <button
                type="button"
                className="btn-ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
